import React, { useState } from 'react';
import moment from 'moment-timezone';

import { Box, Button, Grid, IconButton, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { Controller, UseFormGetValues, useFormContext } from 'react-hook-form';
import { RiDeleteBinLine } from 'react-icons/ri';
import { DeliveryPeriod, DeliveryTime } from 'common/types';
import DatePickerField from './DatePickerField';
import DeliveryTimes from './DeliveryTimes';

import { useTranslation } from 'services/localization/useTranslation';
import { DeliveryOptionForm, deliveryTimesHaveDuplicateSlots } from '../utils';

interface Props {
	index: number;
	value: Partial<DeliveryPeriod>;
	isLastPeriod: boolean;
	onDelete: () => void;
	handleAddDeliveryPeriod: () => void;
	getValues: UseFormGetValues<DeliveryOptionForm>;
}

const SingleDeliveryPeriod = (props: Props) => {
	const { index, value, isLastPeriod, onDelete, handleAddDeliveryPeriod, getValues } = props;
	const classes = useStyles();
	const { t } = useTranslation();
	const { control, formState } = useFormContext();
	const error = formState.errors?.deliveryPeriods?.[index];

	const [hasEndDate, setHasEndDate] = useState(!!value.endDate);

	const currentStartDate = getValues().deliveryPeriods?.[index]?.startDate;

	const deliveryTimesRules = {
		validate: {
			required: (value: DeliveryTime[]) =>
				value.length > 0 || t('common:validation.cantBeEmpty', "Can't be empty"),
			unique: (value: DeliveryTime[]) => !deliveryTimesHaveDuplicateSlots(value),
		},
	};

	const enableEndDate = () => {
		setHasEndDate(true);
	};

	return (
		<Box mt={4}>
			<Grid container alignItems="flex-end" spacing={3}>
				<Grid item xs={10} md={6}>
					<Grid container spacing={3} alignItems="flex-end">
						<Grid item xs={12} sm={6}>
							<Typography variant="body2" color="secondary" gutterBottom>
								{t('common:times.validFrom', 'Valid from')}
							</Typography>
							<Controller
								name={`deliveryPeriods[${index}].startDate`}
								render={({ field }) => (
									<DatePickerField id={`deliveryPeriods[${index}].startDate`} {...field} />
								)}
								control={control}
								defaultValue={value.startDate}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<Typography variant="body2" color="secondary" gutterBottom>
								{t('common:times.until', 'Until')}
							</Typography>
							<Controller
								name={`deliveryPeriods[${index}].endDate`}
								render={({ field }) =>
									hasEndDate ? (
										<DatePickerField
											id={`deliveryPeriods[${index}].endDate`}
											minDate={currentStartDate}
											{...field}
											value={field.value ?? moment(currentStartDate).format('YYYY-MM-DD')}
										/>
									) : (
										<Button variant="text" color="primary" onClick={enableEndDate}>{`+ ${t(
											'delivery.addEndDate',
											'Add end date',
										)}`}</Button>
									)
								}
								control={control}
								defaultValue={value.endDate}
							/>
						</Grid>
					</Grid>
				</Grid>
				{hasEndDate && (
					<Grid item xs={2}>
						<IconButton onClick={onDelete} size="large">
							<RiDeleteBinLine />
						</IconButton>
					</Grid>
				)}
			</Grid>
			<Box mt={2} sx={{ background: (theme) => theme.palette.background.default, p: 2 }}>
				<Controller
					name={`deliveryPeriods[${index}].times`}
					rules={deliveryTimesRules}
					render={({ field }) => (
						<DeliveryTimes
							error={!!error?.times ? error?.times : undefined}
							deleteDeliveryPeriod={onDelete}
							{...field}
						/>
					)}
					control={control}
					defaultValue={value.times}
				/>
			</Box>
			{isLastPeriod && hasEndDate && (
				<Box mt={3}>
					<Button
						variant="outlined"
						color="primary"
						className={classes.button}
						onClick={handleAddDeliveryPeriod}
					>{`+ ${t('delivery.addPeriod', 'Add period')}`}</Button>
				</Box>
			)}
		</Box>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		button: {
			minHeight: 32,
			maxHeight: 32,
		},
	}),
);

export default SingleDeliveryPeriod;
